import React, { useContext } from "react"
import { Link } from "gatsby"
import styled from "styled-components"
import { ContentfulLink, ParagraphWithRemarkFragment } from "../../types/graphql-types"
import { RootLayout, breakpoints as bp, padding } from "./layout"
import { HmdGlobalLogo } from "./logo"
import colors from "../utils/colors"
import LocaleContext from "../context/localeContext"
import CookieConsent from "./cookieConsent"
import { GooglePlayCta, AppStoreCta, WebStoreCta } from "./downloadButtons"
import { createLocalizedPath } from "../utils/urlUtils"

export interface FootProps {
  isInverted?: boolean
  links?: ContentfulLink[] | null
  description?: string
}

interface AppWindow extends Window {
  gravitoLightCMP: any
}
declare const window: AppWindow

const Container = styled(RootLayout as any)<{ isInverted?: boolean }>`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  background-color: ${props => (props.isInverted ? colors.dark : colors.white)};
  color: ${props => (props.isInverted ? colors.white : colors.black)};
  height: auto;
  padding: ${padding.mobile};
  border-top: ${props =>
    props.isInverted ? `1px solid ${colors.grayLight10}` : `2px solid ${colors.gray10}`};

  a {
    color: ${props => (props.isInverted ? colors.white : colors.black)};
  }
  @media (min-width: ${bp.tabletMin}) {
    padding: ${padding.mobile};
  }

  @media (min-width: ${bp.desktopMin}) {
    padding: ${padding.desktop};
    flex-direction: row;
  }
`

const LogoContainer = styled.div<{ isInverted?: boolean }>`
  order: 1;
  a {
    color: ${props => (props.isInverted ? colors.white : colors.dark)};
  }
  @media (min-width: ${bp.tabletMin}) {
    text-align: center;
  }

  @media (min-width: ${bp.desktopMin}) {
    text-align: left;
    margin: auto;
  }
`

const Copyright = styled.span`
  order: 5;
  color: #888888;
  font-size: 14px;
  padding-left: 15px;

  @media (min-width: ${bp.desktopMin}) {
    order: 5;
    text-align: right;
    flex: 1;
    align-self: flex-end;
    flex-basis: 20%;
  }
`

const Separator = styled.div`
  display: none;

  @media (max-width: ${bp.mobileMax}) {
    order: 4;
    display: block;
    width: 100%;
    margin: ${padding.mobile} 0;
  }
`

const Links = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  order: 5;
  a {
    height: 100%;
    padding: 0 ${padding.mobile} 15px ${padding.mobile};
    font-weight: 500;
    color: ${colors.teal};
  }

  @media (min-width: ${bp.tabletMin}) {
    flex-direction: row;
  }

  @media (min-width: ${bp.desktopMin}) {
    order: 3;
    flex-basis: 80%;
    justify-content: flex-end;
    flex: 1;
    a {
      &:first-of-type {
        padding-left: 0;
      }
      &:last-of-type {
        padding-right: 0;
      }
    }
  }
`

const Buttons = styled.div<{ isInverted?: boolean }>`
  display: flex;
  flex-direction: column;
  margin-top: ${padding.mobile};
  order: 2;
  button {
    background-color: ${props => (props.isInverted ? colors.white : colors.dark)};
    color: ${props => (props.isInverted ? colors.dark : colors.white)};
  }

  @media (min-width: ${bp.tabletMin}) {
    order: 2;
    flex-direction: row;
    justify-content: center;
    margin-bottom: ${padding.mobile};
    button {
      margin: 0 ${padding.mobile};
      &:last-of-type {
        margin-right: 0;
      }
      &:first-of-type {
        margin-left: 0;
      }
    }
  }

  @media (min-width: ${bp.desktopMin}) {
    flex-direction: row;
    padding: 0;
    margin: 0;
    margin-top: 40px;
    order: 4;
    justify-content: flex-start;
    flex-basis: 100%;
    margin-right: auto;
    width: auto;
    button {
      margin: 0 ${padding.mobile};
      &:last-of-type {
        margin-right: 0;
      }
      &:first-of-type {
        margin-left: 0;
      }
    }
  }
`

const Foot = (props: FootProps) => {
  const locale = useContext(LocaleContext)
  const { links, isInverted, description } = props
  return (
    <>
      <Container isInverted={isInverted}>
        <LogoContainer isInverted={isInverted}>
          <HmdGlobalLogo />
        </LogoContainer>
        {links && (
          <Buttons isInverted={isInverted}>
            {links.map(cta =>
              cta?.type === "button" ? (
                <>
                  {cta.preset === "google-play" && (
                    <GooglePlayCta
                      key={cta?.gtm!}
                      url={cta?.url!}
                      gtm={cta?.gtm!}
                      content={cta?.heading!}
                    />
                  )}
                  {cta.preset === "app-store" && (
                    <AppStoreCta
                      key={cta?.gtm!}
                      url={cta?.url!}
                      gtm={cta?.gtm!}
                      content={cta?.heading!}
                    />
                  )}
                  {cta.preset === "web-app" && (
                    <WebStoreCta
                      key={cta?.gtm!}
                      url={cta?.url!}
                      gtm={cta?.gtm!}
                      content={cta?.heading!}
                    />
                  )}
                </>
              ) : null
            )}
          </Buttons>
        )}

        <Separator />

        {links && (
          <Links>
            {links.map(
              link =>
                link?.type === "link" && (
                  <Link
                    key={link?.heading!}
                    to={createLocalizedPath(locale, link?.url!)}
                    data-gtm-cta={link?.gtm!}
                    onClick={
                      link?.url === "#cookie-settings"
                        ? e => {
                            e.preventDefault()
                            window["gravitoLightCMP"]?.openPreferences()
                          }
                        : undefined
                    }
                  >
                    {link?.heading}
                  </Link>
                )
            )}
          </Links>
        )}

        {description && <Copyright>{description}</Copyright>}
      </Container>
      <CookieConsent locale={locale || "en"} />
    </>
  )
}

export default Foot
